import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { TAB } from '@angular/cdk/keycodes';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyChip as MatChip, MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { Observable } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsSnackbarService } from 'tsui';
import { AccountService } from 'app/core';
import { Dictionary } from 'app/shared/models';
import { RelatedToType } from 'app/shared/enums';

declare interface DialogData {
  account_id: string;
  related_to_type: RelatedToType;
  selected_items_id: number[];
}

declare type TagActions = 'add' | 'remove' | 'clear';

@Component({
  selector: 'mass-update-tags',
  templateUrl: './mass-update-tags.component.html',
  styleUrls: ['./mass-update-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MassUpdateTagsDialog extends OnDestroyMixin implements OnInit {
  public relatedTags$: Observable<Dictionary<number>>;
  public tagsError$: Observable<HttpErrorResponse>;
  public separatorKeysCodes: number[] = [TAB];
  public tagsToBeAdded: string[] = [];
  public tagsToBeRemoved: string[] = [];
  public newInputTypedTags: string[] = [];
  public isProcessing: boolean = false;

  get relatedToType(): RelatedToType {
    return this.dialogData.related_to_type;
  }

  get selectedItemsId(): number[] {
    return this.dialogData.selected_items_id;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly dialogData: DialogData,
    private readonly accountService: AccountService,
    private readonly matDialogRef: MatDialogRef<MassUpdateTagsDialog>,
    private readonly snackBar: TsSnackbarService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchRelatedTags();
  }

  private fetchRelatedTags(): void {
    this.tagsError$ = this.accountService.accountTagsError$;
    this.relatedTags$ = this.accountService.getAccountTags(this.relatedToType);
  }

  public isSaveBtnDisabled(): boolean {
    return (
      (this.tagsToBeAdded.length === 0 && this.tagsToBeRemoved.length === 0 && this.newInputTypedTags.length === 0) ||
      this.isProcessing
    );
  }

  public discard(): void {
    this.matDialogRef.close();
  }

  public save(): void {
    this.matDialogRef.disableClose = true;
    this.isProcessing = true;
    const params = {
      related_to_type: this.relatedToType,
      related_to_ids: this.selectedItemsId,
      added_tags: [...this.tagsToBeAdded, ...this.newInputTypedTags],
      removed_tags: this.tagsToBeRemoved,
    };

    this.accountService
      .massUpdateTags(params)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (result) => {
          this.isProcessing = false;
          this.matDialogRef.close(result);
          this.snackBar.open('Tags updated successfully', 'success');
        },
        error: () => {
          this.isProcessing = false;
          this.snackBar.open('Failed to update tags', 'error');
        },
      });
  }

  public hasRelatedTags(relatedTagsObj: Dictionary<number>): boolean {
    return relatedTagsObj !== null && Object.keys(relatedTagsObj).length > 0;
  }

  /**
   * Apply custom background colors to the chip when it is clicked.
   * Check if the tag is already added to the list of tags to be added or removed.
   * Push the tag to the corresponding list.
   * @param chip MatChip
   * @param action add | remove | clear
   */
  public onTagClick(chip: MatChip, action: TagActions): void {
    chip._elementRef.nativeElement.classList.toggle('added-chip', action === 'add');
    chip._elementRef.nativeElement.classList.toggle('removed-chip', action === 'remove');

    if (action === 'add' && !this.tagsToBeAdded.includes(chip.value)) {
      this.tagsToBeAdded.push(chip.value);
      this.tagsToBeRemoved = this.tagsToBeRemoved.filter((tag) => tag !== chip.value);
    } else if (action === 'remove' && !this.tagsToBeRemoved.includes(chip.value)) {
      this.tagsToBeRemoved.push(chip.value);
      this.tagsToBeAdded = this.tagsToBeAdded.filter((tag) => tag !== chip.value);
    } else if (action === 'clear') {
      chip._elementRef.nativeElement.classList.remove('added-chip', 'removed-chip');
      this.tagsToBeAdded = this.tagsToBeAdded.filter((tag) => tag !== chip.value);
      this.tagsToBeRemoved = this.tagsToBeRemoved.filter((tag) => tag !== chip.value);
    }
  }

  public addNewTag(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;

    if ((input.value || '').trim()) this.newInputTypedTags.push(input.value.trim());

    if (input) input.value = '';
  }

  public removeNewTag(tag: string): void {
    const index = this.newInputTypedTags.indexOf(tag);
    if (index >= 0) this.newInputTypedTags.splice(index, 1);
  }
}
